import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from './Footer';
import './Activities.css';
import HydrationTracker from './HydrationTracker';
import { ACTIVITY_CATEGORIES, CategoryIcon, formatDuration, getCategoryInfo } from './activityShared';
import {
  getCurrentActivityUserId,
  storeActivities,
  getLocalDateString,
  normalizeActivityDate,
  normalizeActivityTime,
} from '../utils/activityStore';
import { fetchActivitiesFromBackend, saveActivityToBackend, updateActivityOnBackend, deleteActivityFromBackend } from '../services/activityService';

function formatDateLabel(dateStr) {
  const safe = normalizeActivityDate(dateStr);
  const today = getLocalDateString();
  const y = new Date();
  y.setDate(y.getDate() - 1);
  const yesterday = getLocalDateString(y);
  if (safe === today) return 'Today';
  if (safe === yesterday) return 'Yesterday';
  return new Date(`${safe}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}




function PlusCircleIcon({ size = 28, color = '#5CB89A' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" fill={color} />
      <path d="M8 12h8M12 8v8" stroke="#FFF" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

function CloudOfflineIcon({ size = 14, color = 'rgba(67,79,77,0.35)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 2l20 20" />
      <path d="M7 8a5 5 0 0 1 7-2c1.5 0 3 1 3.5 2.5" />
      <path d="M18 18H7a4 4 0 0 1-1-7.8" />
    </svg>
  );
}

function CloseIcon({ size = 22, color = '#434F4D' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

function SpinnerIcon({ size = 16, color = '#5CB89A' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className="activity-spinner">
      <circle cx="12" cy="12" r="10" stroke={color} strokeWidth="3" strokeOpacity="0.25" />
      <path d="M22 12a10 10 0 0 0-10-10" stroke={color} strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

// ─── SUB-COMPONENTS ───

function TodaySummaryCard({ activities }) {
  const todayKey = getLocalDateString();
  const today = activities.filter(a => normalizeActivityDate(a.date) === todayKey);
  const totalMin = today.reduce((s, a) => s + a.durationMin, 0);
  const totalCal = today.reduce((s, a) => s + (a.calories || 0), 0);

  return (
    <div className="card activities-card">
      <h2 className="activities-card-title">Today&rsquo;s Summary</h2>
      <div className="activities-summary-row">
        <div className="activities-summary-item">
          <span className="activities-summary-value">{today.length}</span>
          <span className="activities-summary-label">Activities</span>
        </div>
        <div className="activities-summary-divider" />
        <div className="activities-summary-item">
          <span className="activities-summary-value">{formatDuration(totalMin)}</span>
          <span className="activities-summary-label">Total Time</span>
        </div>
        <div className="activities-summary-divider" />
        <div className="activities-summary-item">
          <span className="activities-summary-value">{totalCal}</span>
          <span className="activities-summary-label">Calories</span>
        </div>
      </div>
    </div>
  );
}

function CategoriesCard() {
  return (
    <div className="card activities-card">
      <h2 className="activities-card-title">Categories</h2>
      <div className="activities-cat-chip-row">
        {ACTIVITY_CATEGORIES.map(cat => (
          <div key={cat.id} className="activities-cat-chip">
            <span className="activities-cat-dot" style={{ backgroundColor: cat.color }} />
            <span className="activities-cat-chip-text">{cat.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ActivityItem({ activity, isSelectionMode, isSelected, onToggleSelect, onEdit }) {
  const cat = getCategoryInfo(activity.category);
  return (
    <div 
      className={`activity-item ${isSelectionMode ? 'selection-mode' : ''} ${isSelected ? 'selected' : ''}`}
      onClick={isSelectionMode ? () => onToggleSelect(activity.id) : undefined}
    >
      {isSelectionMode && (
        <div className="activity-checkbox" onClick={(e) => { e.stopPropagation(); onToggleSelect(activity.id); }}>
          <div className={`checkbox-circle ${isSelected ? 'checked' : ''}`}>
            {isSelected && (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            )}
          </div>
        </div>
      )}
      <div className="activity-item-icon" style={{ backgroundColor: `${cat.color}26` }}>
        <CategoryIcon id={cat.id} size={22} color={cat.color} />
      </div>
      <div className="activity-item-info">
        <span className="activity-item-type">{activity.type}</span>
        <span className="activity-item-meta">
          {activity.time} · {formatDuration(activity.durationMin)}
          {activity.calories ? ` · ${activity.calories} cal` : ''}
        </span>
      </div>
      <div className="activity-item-right">
        <div className="activity-intensity-badge" style={{ backgroundColor: `${cat.color}26`, color: cat.color }}>
          {activity.intensity}
        </div>
        {activity.synced === false && (
          <div className="activity-sync-offline" title="Not synced">
            <CloudOfflineIcon size={14} />
          </div>
        )}
        {/* Edit kalem ikonu — selection mode'da gizleniyor çünkü bulk
            delete akışında tıklamalar checkbox'a gidiyor. */}
        {!isSelectionMode && onEdit && (
          <button
            type="button"
            className="activity-item-edit-btn"
            onClick={(e) => { e.stopPropagation(); onEdit(activity); }}
            title="Edit activity"
            aria-label="Edit activity"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

function AddActivityModal({ open, onClose, onSave, form, setForm, isSaving, errorMsg, setErrorMsg, isEditing }) {
  // Inline visibility for the start-time wheel picker. Kept local to the
  // modal so it auto-resets each time the modal opens (no chance of
  // stale "still open" state hanging around).
  const [showTimePicker, setShowTimePicker] = useState(false);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Clear error and close any expanded sub-pickers when the modal opens
  useEffect(() => {
    if (open) {
      setErrorMsg(null);
      setShowTimePicker(false);
    }
  }, [open, setErrorMsg]);

  if (!open) return null;

  return (
    <div className="activity-modal-overlay" onClick={onClose}>
      <div className="activity-modal-card" onClick={(e) => e.stopPropagation()}>
        {/* Error Banner inside Modal */}
        {errorMsg && (
          <div className="activity-modal-error">
            {errorMsg}
            <button onClick={() => setErrorMsg(null)} className="activity-modal-error-close" type="button">
              ×
            </button>
          </div>
        )}

        {/* Header */}
        <div className="activity-modal-header">
          <button className="activity-modal-btn-cancel" onClick={onClose} type="button">
            Cancel
          </button>
          <h3 className="activity-modal-title">{isEditing ? 'Edit Activity' : 'New Activity'}</h3>
          <button
            className="activity-modal-btn-save"
            onClick={onSave}
            disabled={isSaving}
            type="button"
          >
            {isSaving ? (isEditing ? 'Updating...' : 'Saving...') : (isEditing ? 'Update' : 'Save')}
          </button>
        </div>

        {/* Body */}
        <div className="activity-modal-body">

          {/* Category */}
          <label className="activity-modal-field-label">Category</label>
          <div className="activity-modal-cat-grid">
            {ACTIVITY_CATEGORIES.map(cat => (
              <button
                key={cat.id}
                type="button"
                className={`activity-modal-cat-option ${form.category === cat.id ? 'is-selected' : ''}`}
                onClick={() => setForm(p => ({ ...p, category: cat.id }))}
                style={form.category === cat.id ? { borderColor: cat.color } : undefined}
              >
                <div className="activity-modal-cat-icon" style={{ backgroundColor: `${cat.color}26` }}>
                  <CategoryIcon id={cat.id} size={22} color={cat.color} />
                </div>
                <span className="activity-modal-cat-name">{cat.name}</span>
              </button>
            ))}
          </div>

          {/* Name */}
          <label className="activity-modal-field-label">Activity Name</label>
          <input
            type="text"
            className="activity-modal-input"
            placeholder="e.g., Morning Run"
            value={form.type}
            onChange={(e) => setForm(p => ({ ...p, type: e.target.value }))}
            onFocus={(e) => {
              setTimeout(() => {
                e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }, 300);
            }}
          />

          {/* Duration & Calories */}
          <div className="activity-modal-row-2">
            <div className="activity-modal-col">
              <label className="activity-modal-field-label">Duration (min)</label>
              <input
                type="number"
                inputMode="numeric"
                className="activity-modal-input"
                placeholder="30"
                value={form.durationMin}
                onChange={(e) => setForm(p => ({ ...p, durationMin: e.target.value }))}
                onFocus={(e) => {
                  setTimeout(() => {
                    e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }, 300);
                }}
              />
            </div>
            <div className="activity-modal-col">
              <label className="activity-modal-field-label">Calories</label>
              <input
                type="number"
                inputMode="numeric"
                className="activity-modal-input"
                placeholder="0"
                value={form.calories}
                onChange={(e) => setForm(p => ({ ...p, calories: e.target.value }))}
                onFocus={(e) => {
                  setTimeout(() => {
                    e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }, 300);
                }}
              />
            </div>
          </div>

          {/* Start Date — defaults to today, user can backdate up
              to 30 days. Native <input type="date"> gives us a
              calendar popover on desktop and the OS picker on
              mobile-web without an extra dependency. */}
          <label className="activity-modal-field-label">Start Date</label>
          {(() => {
            const todayIso = new Date().toISOString().split('T')[0];
            const minDate = new Date();
            minDate.setDate(minDate.getDate() - 30);
            const minIso = minDate.toISOString().split('T')[0];
            return (
              <input
                type="date"
                className="activity-modal-input activity-modal-date-input"
                value={form.startDate}
                min={minIso}
                max={todayIso}
                onChange={(e) => setForm(p => ({ ...p, startDate: e.target.value }))}
              />
            );
          })()}

          {/* Start Time — when the activity actually began.
              Defaults to "now"; user can backdate to log past activities.
              Two clickable lists (hours 0–23, minutes in 5-min steps) so
              we don't pull in a date-picker dependency for one field. */}
          <label className="activity-modal-field-label">Start Time</label>
          <button
            type="button"
            className="activity-modal-input activity-modal-time-trigger"
            onClick={() => setShowTimePicker(p => !p)}
          >
            <span className="activity-modal-time-label">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              {form.startTime}
            </span>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ transform: showTimePicker ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s ease' }}
              aria-hidden="true"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
          {showTimePicker && (() => {
            const [hh, mm] = form.startTime.split(':');
            const currentHour = parseInt(hh, 10) || 0;
            const currentMin = parseInt(mm, 10) || 0;
            const HOURS = Array.from({ length: 24 }, (_, i) => i);
            const MINUTES = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
            // Round the user's current minute (e.g. 23) down to the nearest
            // 5-minute increment so the highlighted option is always one
            // that exists in our list.
            const roundedMin = MINUTES.reduce((acc, m) =>
              Math.abs(m - currentMin) < Math.abs(acc - currentMin) ? m : acc, MINUTES[0]
            );
            const setHour = (h) => setForm(p => ({
              ...p,
              startTime: `${String(h).padStart(2, '0')}:${String(roundedMin).padStart(2, '0')}`,
            }));
            const setMin = (m) => setForm(p => ({
              ...p,
              startTime: `${String(currentHour).padStart(2, '0')}:${String(m).padStart(2, '0')}`,
            }));
            return (
              <div className="activity-modal-time-wheel">
                <div className="activity-modal-time-col">
                  <div className="activity-modal-time-header">Hour</div>
                  <div className="activity-modal-time-scroll">
                    {HOURS.map(h => {
                      const selected = h === currentHour;
                      return (
                        <button
                          type="button"
                          key={`h-${h}`}
                          className={`activity-modal-time-option ${selected ? 'is-selected' : ''}`}
                          onClick={() => setHour(h)}
                        >
                          {String(h).padStart(2, '0')}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="activity-modal-time-col">
                  <div className="activity-modal-time-header">Minute</div>
                  <div className="activity-modal-time-scroll">
                    {MINUTES.map(m => {
                      const selected = m === roundedMin;
                      return (
                        <button
                          type="button"
                          key={`m-${m}`}
                          className={`activity-modal-time-option ${selected ? 'is-selected' : ''}`}
                          onClick={() => setMin(m)}
                        >
                          {String(m).padStart(2, '0')}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Notes */}
          <label className="activity-modal-field-label">Notes</label>
          <textarea
            className="activity-modal-input activity-modal-textarea"
            placeholder="How did it go?"
            rows={4}
            value={form.note}
            onChange={(e) => setForm(p => ({ ...p, note: e.target.value }))}
            onFocus={(e) => {
              setTimeout(() => {
                e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }, 300);
            }}
          />
        </div>
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ───

function Activities() {
  const location = useLocation();
  const currentUserId = getCurrentActivityUserId();
  const [activities, setActivities] = useState([]);
  const [showModal, setShowModal] = useState(false);
  // editingId — null ise modal yeni aktivite ekliyor, dolu ise mevcut
  // aktiviteyi güncelliyor. handleSave bu state'e bakıp POST mi PUT mi
  // karar veriyor.
  const [editingId, setEditingId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [bulkDeleteMode, setBulkDeleteMode] = useState(false);
  const [selectedActivities, setSelectedActivities] = useState(new Set());

  // Default start time = now in HH:MM. Most users log activities right
  // after doing them, so this saves a tap; users can backdate it freely
  // (e.g. logging this morning's run in the evening).
  const defaultStartTime = () => {
    const n = new Date();
    return `${String(n.getHours()).padStart(2, '0')}:${String(n.getMinutes()).padStart(2, '0')}`;
  };

  // startDate companion to startTime. Defaults to today; the picker
  // accepts the last 30 days back from today via a native HTML
  // <input type="date"> with min/max bounds.
  const defaultStartDate = () => getLocalDateString();

  const [form, setForm] = useState({
    type: '',
    category: 'sports',
    durationMin: '',
    calories: '',
    note: '',
    startTime: defaultStartTime(),
    startDate: defaultStartDate(),
  });

  // Handle deep-link from Insights Quick Actions
  useEffect(() => {
    const state = location.state;
    if (state?.openModal && state?.preselectedCategory) {
      setForm(prev => ({
  ...prev,
  category:
    state.preselectedCategory === 'exercise'
      ? 'sports'
      : state.preselectedCategory === 'sex'
      ? 'other'
      : state.preselectedCategory
}));
      setShowModal(true);
      // Clear state so modal doesn't re-open on back/forward
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  // Load activities from backend on mount
  useEffect(() => {
    loadActivities();
  }, [currentUserId]);


const loadActivities = async () => {
  setIsLoading(true);

  try {
    const fetched = await fetchActivitiesFromBackend();

    const nextActivities = Array.isArray(fetched) ? fetched : [];

    setActivities(nextActivities);
    storeActivities(nextActivities, currentUserId);
  } catch (e) {
    console.error('Failed to load activities:', e);
    setActivities([]);
    storeActivities([], currentUserId);
  } finally {
    setIsLoading(false);
  }
};
 const openAddModal = () => {
  setEditingId(null);
  setForm({
    type: '',
    category: 'sports',
    durationMin: '',
    calories: '',
    note: '',
    startTime: defaultStartTime(),
  });

  setShowModal(true);
};

// Edit modunda modal'ı aç: form'u mevcut activity ile doldur, editingId'yi
// set et. handleSave bunu görüp PUT atacak.
const openEditModal = (activity) => {
  setEditingId(activity.id);
  setForm({
    type: activity.type || '',
    category: activity.category || 'sports',
    durationMin: String(activity.durationMin ?? ''),
    calories: String(activity.calories ?? ''),
    note: activity.note || '',
    startTime: activity.time || defaultStartTime(),
    startDate: activity.date || defaultStartDate(),
  });
  setErrorMsg(null);
  setShowModal(true);
};

  const handleSave = async () => {
    if (!form.type.trim()) {
      setErrorMsg('Please enter an activity name.');
      return;
    }
    const duration = parseInt(form.durationMin, 10) || 0;
    if (duration <= 0) {
      setErrorMsg('Please enter a valid duration.');
      return;
    }
    setErrorMsg(null);

    const calories = parseInt(form.calories, 10) || 0;

    // Intensity logic (matches mobile)
    let calculatedIntensity = 'Moderate';
    if (duration > 0) {
      const cpm = calories / duration;
      if (form.category === 'sleep' || form.category === 'rest' || form.category === 'personal_hygiene') {
        calculatedIntensity = 'Light';
      }  else if (['exercise', 'manual_work', 'sports', 'walking'].includes(form.category)) {
        if (cpm < 4) calculatedIntensity = 'Light';
        else if (cpm < 9) calculatedIntensity = 'Moderate';
        else calculatedIntensity = 'Vigorous';
      } else {
        calculatedIntensity = 'Light';
      }
    }

    // Form'dan gelen date/time'ı newActivity'ye yazmadan önce normalize et;
    // böylece undefined / boş kalırsa bile sıralama (b.date.localeCompare)
    // ileride patlamaz, ekstra olarak modal'da bir bug olsa bile bugünkü
    // tarih fallback olarak gelir.
    const selectedDate = normalizeActivityDate(form.startDate);
    const selectedTime = normalizeActivityTime(form.startTime, defaultStartTime());

    // ─── EDIT MODE ──────────────────────────────────────────────
    // editingId varsa, mevcut aktiviteyi güncelle. Optimistic update
    // listeyi anında günceller, backend PUT arkada koşar; başarısız
    // olursa eski hâle dönülmez (basitlik için), bir sonraki backend
    // fetch düzeltir.
    if (editingId) {
      const updated = {
        id: editingId,
        date: selectedDate,
        time: selectedTime,
        type: form.type,
        category: form.category,
        durationMin: duration,
        intensity: calculatedIntensity,
        calories,
        note: form.note,
        synced: false,
      };

      setActivities((prev) => {
        const nextActivities = prev.map((a) => (a.id === editingId ? updated : a));
        storeActivities(nextActivities, currentUserId);
        return nextActivities;
      });

      // Reset form + modal kapat
      setForm({ type: '', category: 'exercise', durationMin: '', calories: '', note: '', startTime: defaultStartTime(), startDate: defaultStartDate() });
      setShowModal(false);
      const savedEditingId = editingId;
      setEditingId(null);

      setIsSaving(true);
      try {
        const success = await updateActivityOnBackend(savedEditingId, updated);
        if (success) {
          setActivities((prev) => {
            const nextActivities = prev.map((a) => (
              a.id === savedEditingId ? { ...a, synced: true } : a
            ));
            storeActivities(nextActivities, currentUserId);
            return nextActivities;
          });
        }
      } catch (e) {
        console.error('Backend update failed:', e);
      } finally {
        setIsSaving(false);
      }
      return;
    }

    // ─── CREATE MODE ────────────────────────────────────────────
    const newActivity = {
      id: String(Date.now()),
      date: selectedDate,
      // Activity start time, NOT log time. User picks it; defaults to "now"
      // but can be changed to log past activities.
      time: selectedTime,
      type: form.type,
      category: form.category,
      durationMin: duration,
      intensity: calculatedIntensity,
      calories,
      note: form.note,
      synced: false,
    };

    // Optimistic update
    setActivities((prev) => {
      const nextActivities = [newActivity, ...prev];
      storeActivities(nextActivities, currentUserId);
      return nextActivities;
    });
    setForm({ type: '', category: 'exercise', durationMin: '', calories: '', note: '', startTime: defaultStartTime(), startDate: defaultStartDate() });
    setShowModal(false);

    // Save to backend
    setIsSaving(true);
    try {
      const success = await saveActivityToBackend(newActivity);
      if (success) {
        setActivities((prev) => {
          const nextActivities = prev.map((activity) => (
            activity.id === newActivity.id
              ? { ...activity, synced: true }
              : activity
          ));
          storeActivities(nextActivities, currentUserId);
          return nextActivities;
        });
      }
    } catch (e) {
      console.error('Backend save failed:', e);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (activityId) => {
    if (!window.confirm('Are you sure you want to delete this activity?')) return;
    
    // Delete from backend first
    try {
      await deleteActivityFromBackend(activityId);
    } catch (e) {
      console.error('Backend delete failed:', e);
    }
    
    // Always delete from localStorage
    setActivities((prev) => {
      const nextActivities = prev.filter((activity) => activity.id !== activityId);
      storeActivities(nextActivities, currentUserId);
      return nextActivities;
    });
  };

  const toggleBulkDeleteMode = () => {
    setBulkDeleteMode(!bulkDeleteMode);
    setSelectedActivities(new Set());
  };

  const toggleActivitySelection = (activityId) => {
    setSelectedActivities((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(activityId)) {
        newSet.delete(activityId);
      } else {
        newSet.add(activityId);
      }
      return newSet;
    });
  };

  const handleBulkDelete = async () => {
    if (selectedActivities.size === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedActivities.size} activities?`)) return;
    
    // Delete from backend for each selected activity
    const deletePromises = Array.from(selectedActivities).map(id => 
      deleteActivityFromBackend(id).catch(e => console.error('Backend delete failed for', id, e))
    );
    await Promise.all(deletePromises);
    
    // Delete from localStorage
    setActivities((prev) => {
      const nextActivities = prev.filter((activity) => !selectedActivities.has(activity.id));
      storeActivities(nextActivities, currentUserId);
      return nextActivities;
    });
    setBulkDeleteMode(false);
    setSelectedActivities(new Set());
  };

  // Group activities by date (desc).
  // Sort: newest date first, newest start time first within a day, and as a
  // final tie-breaker the most recently CREATED record first (id is a
  // millisecond timestamp at creation, so larger id = added later).
  // Without the id tie-breaker, two activities with the same start time
  // would render in whatever order the backend happened to return them in,
  // which made the list look "shuffled" after refreshes.
  // Group activities by date (desc).
  // Sort: newest date first, newest start time first within a day, and as a
  // final tie-breaker the most recently CREATED record first (id is a
  // millisecond timestamp at creation, so larger id = added later).
  // CRASH-SAFE: backend bir kayıtta `date` veya `time` boş döndürürse
  // localeCompare(undefined) atmasını engellemek için her aktiviteyi sort'tan
  // önce normalize ediyoruz; eksik alanlar bugün / 00:00 fallback'ına düşer.
  const grouped = useMemo(() => {
    const map = {};
    [...activities]
      .map((activity) => ({
        ...activity,
        id: String(activity.id || Date.now()),
        date: normalizeActivityDate(activity.date),
        time: normalizeActivityTime(activity.time, '00:00'),
      }))
      .sort((a, b) =>
        b.date.localeCompare(a.date) ||
        b.time.localeCompare(a.time) ||
        b.id.localeCompare(a.id)
      )
      .forEach(a => {
        if (!map[a.date]) map[a.date] = [];
        map[a.date].push(a);
      });
    return Object.entries(map);
  }, [activities]);

  return (
    <div className="activities-page-wrapper">
      <Navbar />

      <div className="activities-container">
        {isLoading && (
          <div className="activities-loading-bar">
            <SpinnerIcon size={16} />
            <span>Loading activities...</span>
          </div>
        )}

        {/* Backend/sync errors only - validation errors shown in modal */}
        {errorMsg && errorMsg.includes('sync') && (
          <div className="activities-error-banner">
            {errorMsg}
            <button onClick={() => setErrorMsg(null)} className="activities-error-close" type="button">
              <CloseIcon size={16} />
            </button>
          </div>
        )}

        <TodaySummaryCard activities={activities} />

        <CategoriesCard />

        <HydrationTracker />
        
        {/* Activity Log */}
        <div className="card activities-card">
          <div className="activities-list-header">
            <h2 className="activities-card-title" style={{ margin: 0 }}>Activity Log</h2>
            <div className="activities-list-header-right">
              {isSaving && <SpinnerIcon size={16} />}
              
              {/* Bulk Delete Button */}
              <button
                className={`activities-bulk-delete-btn ${bulkDeleteMode ? 'active' : ''}`}
                onClick={toggleBulkDeleteMode}
                title={bulkDeleteMode ? 'Cancel selection' : 'Select activities to delete'}
                type="button"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
              </button>
              
              {bulkDeleteMode && selectedActivities.size > 0 && (
                <button
                  className="activities-confirm-delete-btn"
                  onClick={handleBulkDelete}
                  title={`Delete ${selectedActivities.size} selected`}
                  type="button"
                >
                  Delete ({selectedActivities.size})
                </button>
              )}
              
              <button className="activities-add-btn" onClick={openAddModal} type="button" aria-label="Add activity">
                <PlusCircleIcon size={30} />
              </button>
            </div>
          </div>

          {grouped.length === 0 ? (
            <div className="activities-empty">
              <p>No activities yet.</p>
              <button className="activities-empty-btn" onClick={openAddModal} type="button">
                Add your first activity
              </button>
            </div>
          ) : (
            grouped.map(([date, items]) => (
              <div key={date} className="activities-date-group">
                <h4 className="activities-date-header">{formatDateLabel(date)}</h4>
                {items.map(item => (
                  <ActivityItem 
                    key={item.id} 
                    activity={item} 
                    isSelectionMode={bulkDeleteMode}
                    isSelected={selectedActivities.has(item.id)}
                    onToggleSelect={toggleActivitySelection}
                    onEdit={openEditModal}
                  />
                ))}
              </div>
            ))
          )}
        </div>
      </div>

      <Footer />

      <AddActivityModal
        open={showModal}
        onClose={() => { setShowModal(false); setEditingId(null); }}
        onSave={handleSave}
        form={form}
        setForm={setForm}
        isSaving={isSaving}
        errorMsg={errorMsg}
        setErrorMsg={setErrorMsg}
        isEditing={!!editingId}
      />
    </div>
  );
}

export default Activities;